import type { BrandReportDto } from '@rw/shared';
import { useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router';
import { Page } from '../../components/Layout';
import { ErrorState, LoadingState } from '../../components/states';
import { Button } from '../../components/ui';
import { useRegions } from '../../hooks/queries';
import { formatDate, formatNumber } from '../../lib/format';
import { useAuth } from '../../stores/auth';
import { TIER_LABEL } from '../events/event-format';
import { formatVnd, useBrandReport } from './reports-api';
import { t as tr, useT } from '../../i18n';

const TRIGGER: Record<BrandReportDto['offers'][number]['trigger'], string> = {
  CAMPAIGN_CELL: 'Capture a sponsored cell',
  EVENT_FINISH: 'Finish the event',
  EVENT_PODIUM: 'Event podium',
};
const dateInput = 'h-10 rounded-xl border border-ink-700 bg-ink-850 px-2 text-sm outline-none focus:border-volt';
const pct = (a: number, b: number) => (b ? `${((a / b) * 100).toFixed(1)}%` : '—');

/** Game owner: a brand's report (to send to the sponsor) — print it to PDF or export CSV. */
export function BrandReportPage() {
  const { brandId = '' } = useParams();
  const role = useAuth((s) => s.user?.role);
  const navigate = useNavigate();
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const q = useBrandReport(brandId, from, to);
  const regions = useRegions();
  const t = useT();
  if (role !== 'SUPER_ADMIN') return <Navigate to="/" replace />;
  const city = (id: string) => regions.data?.find((r) => r.id === id)?.name ?? id;
  const r = q.data;
  return (
    <Page>
      <div className="rw-no-print">
        <button onClick={() => navigate(-1)} className="mb-2 text-sm font-semibold text-ink-300">
          ← {t('Back')}
        </button>
        <div className="flex flex-wrap items-end gap-2">
          <label className="text-xs text-ink-400">
            {t('From')}
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className={`${dateInput} block`} data-testid="report-from" />
          </label>
          <label className="text-xs text-ink-400">
            {t('To')}
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className={`${dateInput} block`} data-testid="report-to" />
          </label>
          <div className="ml-auto flex gap-2">
            <Button size="sm" variant="secondary" disabled={!r} onClick={() => r && downloadCsv(r, city)} data-testid="report-csv">
              CSV
            </Button>
            <Button size="sm" disabled={!r} onClick={() => window.print()} data-testid="report-print">
              {t('Print / PDF')}
            </Button>
          </div>
        </div>
      </div>
      {q.isPending ? (
        <LoadingState />
      ) : q.isError ? (
        <ErrorState error={q.error} onRetry={() => q.refetch()} />
      ) : r ? (
        <article className="rw-print mt-5 space-y-6" data-testid="brand-report">
          <header className="flex items-center gap-4">
            {r.brand.logoUrl ? <img src={r.brand.logoUrl} alt={r.brand.name} className="h-14 w-24 object-contain" /> : null}
            <div>
              <div className="text-xs uppercase tracking-widest text-ink-400">{t('Runner Wars · sponsor report')}</div>
              <h1 className="font-display text-3xl font-extrabold uppercase">{r.brand.name}</h1>
              <div className="text-sm text-ink-400">
                {r.from || r.to ? `${r.from ? formatDate(r.from) : '…'} – ${r.to ? formatDate(r.to) : '…'}` : t('All time')} · {t('generated {date}', { date: formatDate(r.generatedAt) })}
              </div>
            </div>
          </header>

          <section className="grid grid-cols-3 gap-2" data-testid="report-totals">
            <Kpi label={t('Map cell views')} value={r.totals.cellViews} />
            <Kpi label={t('Cell taps')} value={r.totals.cellClicks} note={pct(r.totals.cellClicks, r.totals.cellViews)} />
            <Kpi label={t('Sponsored cells taken')} value={r.totals.captures} />
            <Kpi label={t('Banner views')} value={r.totals.bannerViews} />
            <Kpi label={t('Banner taps')} value={r.totals.bannerClicks} note={pct(r.totals.bannerClicks, r.totals.bannerViews)} />
            <Kpi label={t('Runners reached')} value={r.totals.runners} />
            <Kpi label={t('Codes given')} value={r.totals.codesGiven} />
            <Kpi label={t('Codes used')} value={r.totals.codesUsed} note={pct(r.totals.codesUsed, r.totals.codesGiven)} />
            <Kpi label={t('Event runners')} value={r.totals.eventParticipants} />
          </section>

          {r.daily.length ? (
            <section>
              <H>{t('Views and taps per day')}</H>
              <DailyChart daily={r.daily} />
            </section>
          ) : null}

          {r.campaigns.length ? (
            <section>
              <H>{t('Campaigns')}</H>
              <Table
                head={['Campaign', 'Cells', 'Views', 'Taps', 'Banner', 'Taken', 'Runners'].map((h) => t(h))}
                rows={r.campaigns.map((c) => [
                  <span key="n">
                    <b>{c.name}</b>
                    <br />
                    <span className="text-xs text-ink-400">
                      {city(c.regionId)} · {formatDate(c.startsAt)} – {formatDate(c.endsAt)}
                    </span>
                  </span>,
                  c.cells,
                  formatNumber(c.cellViews),
                  formatNumber(c.cellClicks),
                  `${formatNumber(c.bannerViews)} / ${formatNumber(c.bannerClicks)}`,
                  c.captures,
                  c.runners,
                ])}
              />
            </section>
          ) : null}

          {r.events.length ? (
            <section>
              <H>{t('Sponsored events')}</H>
              <Table
                head={['Event', 'Tier', 'Runners', 'Finishers'].map((h) => t(h))}
                rows={r.events.map((e) => [
                  <span key="n">
                    <b>{e.name}</b>
                    <br />
                    <span className="text-xs text-ink-400">
                      {formatDate(e.startsAt)} – {formatDate(e.endsAt)}
                    </span>
                  </span>,
                  t(TIER_LABEL[e.tier]),
                  e.participants,
                  e.finishers,
                ])}
              />
            </section>
          ) : null}

          {r.offers.length ? (
            <section>
              <H>{t('Offers')}</H>
              <Table head={['Offer', 'Earned by', 'Codes', 'Given', 'Used'].map((h) => t(h))} rows={r.offers.map((o) => [o.title, `${t(TRIGGER[o.trigger])}${o.source ? `: ${o.source}` : ''}`, o.codes, o.given, o.used])} />
            </section>
          ) : null}

          <section>
            <H>{t('Sponsorship')}</H>
            <div className="mb-2 grid grid-cols-3 gap-2">
              <Kpi label={t('Cash pledged')} value={formatVnd(r.ledger.cashPledgedVnd)} />
              <Kpi label={t('Cash received')} value={formatVnd(r.ledger.cashReceivedVnd)} />
              <Kpi label={t('Goods (value)')} value={formatVnd(r.ledger.inKindValueVnd)} />
            </div>
            {r.ledger.entries.length ? (
              <Table
                head={['Date', 'What', 'For', 'Received'].map((h) => t(h))}
                rows={r.ledger.entries.map((e) => [
                  formatDate(e.pledgedAt),
                  e.kind === 'CASH' ? formatVnd(e.amountVnd) : `${e.items}${e.amountVnd ? ` (≈ ${formatVnd(e.amountVnd)})` : ''}`,
                  e.event?.name ?? e.campaign?.name ?? '—',
                  e.receivedAt ? formatDate(e.receivedAt) : '—',
                ])}
              />
            ) : (
              <p className="text-sm text-ink-500">{t('Nothing recorded for this period.')}</p>
            )}
          </section>
          <p className="text-xs text-ink-500">{t('Views and taps are counted per day; no runner or location is stored.')}</p>
        </article>
      ) : null}
    </Page>
  );
}

function H({ children }: { children: React.ReactNode }) {
  return <h2 className="mb-2 text-xs font-bold uppercase tracking-widest text-ink-400">{children}</h2>;
}

function Kpi({ label, value, note }: { label: string; value: number | string; note?: string }) {
  return (
    <div className="rounded-xl border border-ink-700 px-2 py-2 text-center">
      <div className="font-display text-xl font-bold tabular">{typeof value === 'number' ? formatNumber(value) : value}</div>
      <div className="text-[11px] text-ink-400">
        {label}
        {note ? ` · ${note}` : ''}
      </div>
    </div>
  );
}

function Table({ head, rows }: { head: string[]; rows: Array<Array<React.ReactNode>> }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-ink-700 text-left text-xs text-ink-400">
            {head.map((h, i) => (
              <th key={h} className={`py-1.5 pr-2 font-semibold ${i ? 'text-right' : ''}`}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-b border-ink-800 align-top">
              {row.map((cell, j) => (
                <td key={j} className={`py-1.5 pr-2 ${j ? 'text-right tabular' : ''}`}>
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DailyChart({ daily }: { daily: BrandReportDto['daily'] }) {
  const t = useT();
  const max = Math.max(1, ...daily.map((d) => d.views));
  const w = 100 / daily.length;
  return (
    <div>
      <svg viewBox="0 0 100 40" preserveAspectRatio="none" className="h-32 w-full">
        {daily.map((d, i) => (
          <g key={d.day}>
            <rect x={i * w + w * 0.1} y={40 - (d.views / max) * 38} width={w * 0.8} height={(d.views / max) * 38} fill="#38bdf8" opacity={0.5} />
            <rect x={i * w + w * 0.3} y={40 - (d.clicks / max) * 38} width={w * 0.4} height={(d.clicks / max) * 38} fill="#c6ff3d" />
          </g>
        ))}
      </svg>
      <div className="flex justify-between text-[11px] text-ink-400">
        <span>{formatDate(daily[0]!.day)}</span>
        <span>
          <span className="text-sky">■</span> {t('views')} · <span className="text-volt">■</span> {t('taps')}
        </span>
        <span>{formatDate(daily[daily.length - 1]!.day)}</span>
      </div>
    </div>
  );
}

/** The report as a CSV (sections one after another) for spreadsheets. */
function downloadCsv(r: BrandReportDto, city: (id: string) => string) {
  const esc = (v: unknown) => {
    const s = String(v ?? '');
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const h = (...cols: string[]) => cols.map((c) => tr(c));
  const TOTAL_LABEL: Record<keyof BrandReportDto['totals'], string> = {
    cellViews: 'Map cell views',
    cellClicks: 'Cell taps',
    bannerViews: 'Banner views',
    bannerClicks: 'Banner taps',
    captures: 'Sponsored cells taken',
    runners: 'Runners reached',
    codesGiven: 'Codes given',
    codesUsed: 'Codes used',
    eventParticipants: 'Event runners',
  };
  const lines: unknown[][] = [
    [tr('Brand'), r.brand.name],
    [tr('From'), r.from ?? tr('all time')],
    [tr('To'), r.to ?? ''],
    [],
    [tr('Totals')],
    ...(Object.entries(r.totals) as Array<[keyof BrandReportDto['totals'], number]>).map(([k, v]) => [tr(TOTAL_LABEL[k]), v]),
    [],
    h('Campaign', 'City', 'Starts', 'Ends', 'Cells', 'Cell views', 'Cell taps', 'Banner views', 'Banner taps', 'Cells taken', 'Runners'),
    ...r.campaigns.map((c) => [c.name, city(c.regionId), c.startsAt.slice(0, 10), c.endsAt.slice(0, 10), c.cells, c.cellViews, c.cellClicks, c.bannerViews, c.bannerClicks, c.captures, c.runners]),
    [],
    h('Event', 'Tier', 'Starts', 'Ends', 'Runners', 'Finishers'),
    ...r.events.map((e) => [e.name, tr(TIER_LABEL[e.tier]), e.startsAt.slice(0, 10), e.endsAt.slice(0, 10), e.participants, e.finishers]),
    [],
    h('Offer', 'Earned by', 'Source', 'Codes', 'Given', 'Used'),
    ...r.offers.map((o) => [o.title, tr(TRIGGER[o.trigger]), o.source, o.codes, o.given, o.used]),
    [],
    h('Pledged', 'Kind', 'Amount (VND)', 'Goods', 'For', 'Received', 'Note'),
    ...r.ledger.entries.map((e) => [e.pledgedAt, tr(e.kind === 'CASH' ? 'Cash' : 'Goods'), e.amountVnd, e.items, e.event?.name ?? e.campaign?.name ?? '', e.receivedAt?.slice(0, 10) ?? '', e.note]),
    [],
    h('Day', 'Views', 'Taps'),
    ...r.daily.map((d) => [d.day, d.views, d.clicks]),
  ];
  // BOM so Excel reads Vietnamese correctly.
  const blob = new Blob(['﻿' + lines.map((l) => l.map(esc).join(',')).join('\n')], { type: 'text/csv;charset=utf-8' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `${r.brand.name.replace(/[^\p{L}\p{N}]+/gu, '-')}-report${r.from ? `-${r.from}` : ''}${r.to ? `-${r.to}` : ''}.csv`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}
